<script>
import { useAlert } from 'dashboard/composables';
import { format, parseISO } from 'date-fns';
import SectionCard from '../SectionCard.vue';
import DateOverrideModal from '../modals/DateOverrideModal.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import {
  listHoursOverrides,
  createHoursOverride,
  deleteHoursOverride,
} from '../../../../api/realEstate/operatingHours';
import { apiErrorMessage } from '../../../../api/realEstate/errors';

// Special dates (Layer 0 overrides) are only shown on this one screen, so
// they're fetched directly here rather than through Vuex — same approach
// AvailabilityPicker.vue already takes for its own single-screen data
// (docs/real-estate/be/10-api-reference.md §A7).
export default {
  name: 'BookingSection',
  components: { SectionCard, DateOverrideModal, DeleteModal },
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      overrides: [],
      isLoadingOverrides: false,
      isAddModalOpen: false,
      overrideToDelete: null,
      isDeleting: false,
    };
  },
  computed: {
    isAgentMode() {
      return this.project.booking_mode === 'agent';
    },
  },
  watch: {
    'project.property_id': {
      immediate: true,
      handler() {
        this.loadOverrides();
      },
    },
  },
  methods: {
    async loadOverrides() {
      this.isLoadingOverrides = true;
      try {
        const { data } = await listHoursOverrides(this.project.property_id, {
          from_date: format(new Date(), 'yyyy-MM-dd'),
        });
        this.overrides = data.results
          .slice()
          .sort((a, b) => (a.override_date < b.override_date ? -1 : 1));
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.OVERRIDES.ERROR_LOAD')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isLoadingOverrides = false;
      }
    },
    formatDate(d) {
      return format(parseISO(d), 'd MMM yyyy');
    },
    async handleSave(body) {
      await createHoursOverride(this.project.property_id, body);
      await this.loadOverrides();
    },
    async confirmDelete() {
      this.isDeleting = true;
      try {
        await deleteHoursOverride(
          this.project.property_id,
          this.overrideToDelete.id
        );
        useAlert(this.$t('REAL_ESTATE.OVERRIDES.SUCCESS_DELETED'));
        this.overrideToDelete = null;
        await this.loadOverrides();
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.OVERRIDES.ERROR_DELETE')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isDeleting = false;
      }
    },
  },
};
</script>

<template>
  <SectionCard section-id="booking" :title="$t('REAL_ESTATE.BOOKING.TITLE')">
    <template #action>
      <woot-button
        variant="clear"
        size="small"
        icon="edit"
        @click="$emit('edit')"
      >
        {{ $t('REAL_ESTATE.BOOKING.EDIT_BUTTON') }}
      </woot-button>
    </template>

    <dl class="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
      <div>
        <dt class="text-slate-400">{{ $t('REAL_ESTATE.BOOKING.MODE') }}</dt>
        <dd class="text-slate-900 dark:text-slate-100">
          {{
            isAgentMode
              ? $t('REAL_ESTATE.BOOKING.MODE_AGENT')
              : $t('REAL_ESTATE.BOOKING.MODE_WALK_IN')
          }}
        </dd>
      </div>
      <div v-if="isAgentMode">
        <dt class="text-slate-400">
          {{ $t('REAL_ESTATE.BOOKING.VIEWING_DURATION') }}
        </dt>
        <dd class="text-slate-900 dark:text-slate-100">
          {{
            $t('REAL_ESTATE.BOOKING.MINUTES', {
              minutes: project.viewing_duration_minutes,
            })
          }}
        </dd>
      </div>
      <div v-if="isAgentMode">
        <dt class="text-slate-400">
          {{ $t('REAL_ESTATE.BOOKING.SLOT_CAPACITY') }}
        </dt>
        <dd class="text-slate-900 dark:text-slate-100">
          {{
            project.slot_capacity == null
              ? $t('REAL_ESTATE.BOOKING.SLOT_CAPACITY_UNLIMITED')
              : project.slot_capacity
          }}
        </dd>
      </div>
    </dl>

    <div
      class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between"
    >
      <div>
        <p class="text-sm text-slate-900 dark:text-slate-100">
          {{ $t('REAL_ESTATE.BOOKING.OPENING_HOURS') }}
        </p>
        <p class="text-xs text-slate-400 mt-0.5">
          {{ project.showroom.opening_hours_text }}
        </p>
      </div>
      <woot-button
        variant="clear"
        size="small"
        icon="edit"
        @click="$emit('editHours')"
      >
        {{ $t('REAL_ESTATE.BOOKING.EDIT_HOURS_BUTTON') }}
      </woot-button>
    </div>

    <DateOverrideModal
      v-if="isAddModalOpen"
      :save="handleSave"
      @close="isAddModalOpen = false"
    />
    <DeleteModal
      v-if="overrideToDelete"
      :delete-name="
        overrideToDelete.reason || formatDate(overrideToDelete.override_date)
      "
      :is-loading="isDeleting"
      @confirm="confirmDelete"
      @close="overrideToDelete = null"
    />

    <div class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
      <div class="flex items-center justify-between mb-2">
        <p class="text-sm text-slate-900 dark:text-slate-100">
          {{ $t('REAL_ESTATE.OVERRIDES.TITLE') }}
        </p>
        <woot-button
          variant="clear"
          size="small"
          icon="add"
          @click="isAddModalOpen = true"
        >
          {{ $t('REAL_ESTATE.OVERRIDES.ADD_BUTTON') }}
        </woot-button>
      </div>

      <p v-if="isLoadingOverrides" class="text-xs text-slate-400">
        {{ $t('REAL_ESTATE.OVERRIDES.LOADING') }}
      </p>
      <p v-else-if="!overrides.length" class="text-xs text-slate-400">
        {{ $t('REAL_ESTATE.OVERRIDES.EMPTY') }}
      </p>
      <ul v-else class="space-y-2">
        <li
          v-for="override in overrides"
          :key="override.id"
          class="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-200"
        >
          <span class="w-24 shrink-0 text-slate-900 dark:text-slate-100">
            {{ formatDate(override.override_date) }}
          </span>
          <span class="w-28 shrink-0">
            {{
              override.closed
                ? $t('REAL_ESTATE.OVERRIDES.CLOSED_LABEL')
                : `${override.start_time}–${override.end_time}`
            }}
          </span>
          <span class="flex-1 text-slate-400 truncate">
            {{ override.reason }}
          </span>
          <woot-button
            variant="clear"
            size="small"
            color-scheme="alert"
            icon="delete"
            @click="overrideToDelete = override"
          />
        </li>
      </ul>
    </div>
  </SectionCard>
</template>
